import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function useNotifications() {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const workerRef = useRef(null);

  // Initialize and handle SharedWorker setup
  useEffect(() => {
    if (typeof window === "undefined" || !("SharedWorker" in window)) {
      console.warn(
        "SharedWorker is not supported in this browser environment.",
      );
      return;
    }

    // Webpack 5 native Shared Worker syntax
    const worker = new SharedWorker(
      new URL("./notifications.worker.js", import.meta.url),
      { name: "notifications-worker" },
    );
    workerRef.current = worker;

    worker.port.onmessage = (event) => {
      const { type, data } = event.data;
      if (type === "UPDATE_NOTIFICATIONS") {
        setNotifications(data);
        setUnreadCount(data.filter((n) => !n.read_at).length);
      }
    };

    worker.port.start();

    // Signal initialization with current details
    if (token) {
      worker.port.postMessage({ type: "INIT", token, apiUrl: API_URL });
    }

    // Clean up communication ports when component or tab unmounts
    return () => {
      if (worker.port) {
        worker.port.postMessage({ type: "CLOSE" });
        worker.port.close();
      }
    };
  }, [token]);

  // Sync token state transitions seamlessly
  useEffect(() => {
    if (workerRef.current?.port) {
      workerRef.current.port.postMessage({
        type: "TOKEN_CHANGED",
        token,
        apiUrl: API_URL,
      });
    }
  }, [token]);

  const fetchNotifications = useCallback(() => {
    // Shared Worker natively handles the pulling and polling optimization,
    // but we can pass an initiation command if explicitly forced by the UI
    if (workerRef.current?.port && token) {
      workerRef.current.port.postMessage({
        type: "INIT",
        token,
        apiUrl: API_URL,
      });
    }
  }, [token]);

  const markAsRead = useCallback(
    async (id) => {
      if (!token) return;
      try {
        // 1. Execute mutations via REST API on the tab that clicked it
        await fetch(`${API_URL}/api/admin/notifications/${id}/read`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });

        // 2. Locally calculate updated data array
        const updatedData = notifications.map((n) =>
          n.id === id ? { ...n, read_at: new Date().toISOString() } : n,
        );

        // 3. Inform the SharedWorker to propagate the state transformation to ALL open tabs
        if (workerRef.current?.port) {
          workerRef.current.port.postMessage({
            type: "MANUAL_UPDATE",
            updatedData,
          });
        }
      } catch (e) {
        console.error("Failed to mark notification as read:", e);
      }
    },
    [token, notifications],
  );

  return { notifications, unreadCount, markAsRead, fetchNotifications };
}
